import React, { useState } from 'react';
import { Trans } from '@lingui/react';
import styled from 'styled-components';
import Button from '@components/buttons';
import Icon from '@components/Icon';
import iconTooltip from '@components/IconTooltip';
import {
  Box,
  Flex,
  ShowInLargeUp,
  ShowInMediumDown,
} from '@components/layout/Grid';
import {
  ProgramCollapsibleTextTitle,
  ProgramCollapsibleTitleMeta,
} from '@components/texts';
import { TextWithIcon } from '@components/TextWithIcon';
import {
  ProgressBar,
  ProgressBarValue,
} from '@ecosystems/programs/ProgramsHeaderSection';
import { GenericProgram, ProgramCategory } from '@gql/generated';

const getAppearanceColor = (theme, appearance, highlight) => {
  if (!highlight) {
    return theme.colors.neutrals['white-darker'];
  } else if (appearance === ProgramCategory['Challenges']) {
    return theme.colors.primary['green-1'];
  } else {
    return theme.colors.primary['blue-4'];
  }
};
const TitleBox = styled.div<{
  isDone?: boolean;
  contentShown?: boolean;
  appearance?: string;
}>`
  border-radius: 3px;
  background-color: ${({ theme, appearance, contentShown, isDone }) => {
    return getAppearanceColor(theme, appearance, contentShown || isDone);
  }};
  cursor: pointer;
`;

const Indicators = styled(Flex)`
  &:empty {
    display: none;
  }
`;

const IconTooltip = styled(iconTooltip)`
  margin-left: 5px;
`;

type Props = {
  children: React.ReactChild | React.ReactChild[];
  title(isHighlighted: boolean): React.ReactNode;
  isDone?: boolean;
  weeks?: number;
  isOpen: boolean;
  onOpen(): void;
  onClose(): void;
  weeksRange?: React.ReactElement | string;
  quiz?: number;
  videosCount?: number;
  progress?: number;
  videosDuration?: string;
  showProgress?: boolean;
  progressTooltipText?: React.ReactElement;
  showProgressTooltip?: boolean;
  appearance?: GenericProgram['category'];
};

const ProgramCollapsibleTitle = (props: Props) => {
  const [isOpen, setIsOpen] = useState(props.isOpen);
  const ref = React.useRef<HTMLDivElement>();

  React.useEffect(() => {
    // prevent scroll on the first render
    if (props.isOpen !== isOpen && props.isOpen) {
      requestAnimationFrame(() => {
        const top = ref.current.offsetTop;
        window.scroll(0, top - 65);
      });
    }
    setIsOpen(props.isOpen);
  }, [props.isOpen]);

  const {
    showProgressTooltip,
    showProgress,
    progressTooltipText = <Trans id="phasedProgram.phase_progress.text" />,
    appearance,
  } = props;

  const renderPhaseWeeksCount = () => {
    return props.weeks ? (
      <Box
        mb={['10px', '10px', '0px']}
        px="20px"
        data-testid="ProgramCollapsibleTitle--WeeksCount"
      >
        <ProgramCollapsibleTitleMeta>
          <TextWithIcon icon="ygb-icon-programlenght-ic" iconSize="18px">
            <Trans
              id={'program.weekCountShort'}
              message={'{weekCount} week'}
              values={{
                weekCount: props.weeks,
              }}
            />
          </TextWithIcon>
        </ProgramCollapsibleTitleMeta>
      </Box>
    ) : null;
  };

  const renderPhaseWeeksRange = () => {
    return props.weeksRange ? (
      <Box
        mb={['10px', '10px', '0px']}
        px="20px"
        data-testid="ProgramCollapsibleTitle--WeeksRange"
      >
        <ProgramCollapsibleTitleMeta>
          <TextWithIcon icon="ygb-icon-calendar-o" iconSize="18px">
            {props.weeksRange}
          </TextWithIcon>
        </ProgramCollapsibleTitleMeta>
      </Box>
    ) : null;
  };

  const renderPhaseVideosDuration = () => {
    return props.videosCount ? (
      <Box
        mb={['10px', '10px', '0px']}
        px="20px"
        flexShrink="0"
        data-testid="ProgramCollapsibleTitle--VideosDuration"
      >
        <ProgramCollapsibleTitleMeta>
          <TextWithIcon icon="ygb-icon-playlist" iconSize="18px">
            <Trans
              id={'program.videosCount'}
              message={'{videoCount} Videos'}
              values={{
                videoCount: props.videosCount,
              }}
            />
            {props.videosDuration && ` - ${props.videosDuration}`}
          </TextWithIcon>
        </ProgramCollapsibleTitleMeta>
      </Box>
    ) : null;
  };

  const renderPhaseQuiz = () => {
    return props.quiz ? (
      <Box as={ShowInLargeUp} px="20px">
        <ProgramCollapsibleTitleMeta>
          <TextWithIcon icon="ygb-icon-quiz-ic" iconSize="18px">
            <Trans
              id={'program.quizCount'}
              message={'{quizCount} quiz'}
              values={{
                quizCount: props.quiz,
              }}
            />
          </TextWithIcon>
        </ProgramCollapsibleTitleMeta>
      </Box>
    ) : null;
  };

  const togglCollapse = () => {
    if (props.isOpen) {
      props.onClose();
    } else {
      props.onOpen();
    }
  };

  return (
    <div ref={ref} data-testid={props['data-testid']}>
      <Box
        as={TitleBox}
        px="20px"
        py="18px"
        mb={[15, 15, 30]}
        isDone={props.isDone}
        contentShown={props.isOpen}
        onClick={togglCollapse}
        appearance={appearance}
        data-testid={`${props['data-testid']}--head`}
      >
        <Flex flexDirection="column">
          <Flex justifyContent="space-between" alignItems="center">
            <Box flex={1} flexShrink={0}>
              <Flex
                flexDirection={['column', 'column', 'row', 'row']}
                alignItems={['flex-start', 'flex-start', 'center']}
                ml="-20px"
              >
                <Box px="20px" mr={[0, 0, '50px']}>
                  <ProgramCollapsibleTextTitle data-testid="ProgramCollapsibleTitle--title">
                    {props.title(props.isOpen)}
                  </ProgramCollapsibleTextTitle>
                </Box>
                <Indicators
                  flexWrap="wrap"
                  justifyContent="center"
                  mt={[15, 15, 0, 0]}
                  mr={[0, 0, '50px']}
                >
                  {renderPhaseWeeksCount()}
                  {renderPhaseWeeksRange()}
                  {renderPhaseVideosDuration()}
                  {renderPhaseQuiz()}
                </Indicators>
              </Flex>
            </Box>
            <Box flex="0 auto">
              <Flex ml="-20px" alignItems="auto">
                <Box px="20px" as={ShowInLargeUp}>
                  {showProgress ? (
                    <Flex alignItems="center">
                      <ProgramCollapsibleTitleMeta
                        style={{ textTransform: 'none' }}
                      >
                        <Trans
                          id={'program.phaseCompletion'}
                          message={'Phase completion: {progress}%'}
                          values={{
                            progress: props.progress,
                          }}
                        />
                      </ProgramCollapsibleTitleMeta>
                      <Box ml="5px" flex={1}>
                        <ProgressBar width="140px">
                          <ProgressBarValue
                            appearance={appearance}
                            progressValue={props.progress}
                          />
                        </ProgressBar>
                      </Box>
                      {showProgressTooltip && (
                        <IconTooltip>{progressTooltipText}</IconTooltip>
                      )}
                    </Flex>
                  ) : null}
                </Box>
                <Box pl="20px">
                  <Button
                    appearance="link"
                    href="#"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Icon
                      name={
                        props.isOpen
                          ? 'ygb-icon-chevron-up'
                          : 'ygb-icon-chevron-down'
                      }
                      color="gray"
                    />
                  </Button>
                </Box>
              </Flex>
            </Box>
          </Flex>
          <Box px={[0, 0, 20]} as={ShowInMediumDown}>
            {showProgress ? (
              <Flex alignItems="center">
                <ProgramCollapsibleTitleMeta style={{ textTransform: 'none' }}>
                  <Trans
                    id={'program.phaseCompletion'}
                    message={'Phase completion: {progress}%'}
                    values={{
                      progress: String(props.progress),
                    }}
                  />
                </ProgramCollapsibleTitleMeta>
                <Box ml="5px" flex={1}>
                  <ProgressBar flex={1}>
                    <ProgressBarValue
                      appearance={appearance}
                      progressValue={props.progress}
                    />
                  </ProgressBar>
                </Box>
              </Flex>
            ) : null}
          </Box>
        </Flex>
      </Box>
      {props.isOpen ? (
        <Box mb="30px" data-testid={`${props['data-testid']}--body`}>
          {props.children}
        </Box>
      ) : null}
    </div>
  );
};

export default ProgramCollapsibleTitle;
